import { UserSession, UserContext } from './../../core/user-session';
import { User } from './user';
import { SiteContextService } from './../../core/site-context.service';
import { TriggerbeeRequester, TriggerbeeApi } from './../../core/triggerbee/triggerbee-requester.service';
import { Logger, LoggerFactory } from './../../core/logger.service';
import { Injectable, EventEmitter } from '@angular/core';
import { ConsentService } from '../../consent/consent.service';

@Injectable()
export class SideMenuService {
  private lastUser: User;
  public userChanged = new EventEmitter<User>();

  private readonly logger: Logger;

  constructor(
    loggerFactory: LoggerFactory,
    private readonly requester: TriggerbeeRequester,
    private readonly userSession: UserSession,
    private readonly siteContext: SiteContextService,
    private readonly consentService: ConsentService,
  ) {
    this.logger = loggerFactory.get('SideMenuService');

    // Pull initial user
    this.getCurrentUserAsync();

    userSession.onContextChanged.subscribe(async (context: UserContext) => {
      await this.refreshUserAsync(context);
    });
  }

  public async getCurrentUserAsync(): Promise<User> {
    if (this.lastUser) {
      this.logger.debug('User already existed so no need to load it again');
      return this.lastUser;
    }
    this.lastUser = await this.refreshUserAsync(this.userSession.context);
    return this.lastUser;
  }

  public async refreshUserAsync(context: UserContext): Promise<User> {
    let user: User;
    // this.logger.debug('Should generate user from userContext: ', context);
    if (context && context.siteId) {
      user = new User();
      user.hasSites = context.hasCurrentSite;
      user.isAdmin = context.isAdmin;
      user.isSiteAdmin = context.isSiteAdmin;
    }
    this.lastUser = user;
    this.userChanged.emit(user);
    return user;
  }

  public async loadConsentAsync(user: User): Promise<User> {
    user.hasConsentAccount = user.hasSites && (await this.hasConsentAccountAsync());
    user.hasConsentWidget = user.hasConsentAccount && (await this.hasConsentWidgetAsync());
    return user;
  }

  private async hasConsentAccountAsync(): Promise<boolean> {
    // Ensure Consent account
    return await this.consentService.hasConsentAccountAsync();
  }

  private async hasConsentWidgetAsync(): Promise<boolean> {
    const currentSite = await this.siteContext.getCurrentSiteAsync();
    if (!currentSite) {
      this.logger.error('User has no current site, therefore no consent account');
      return false;
    }
    const siteId = currentSite.id;
    const response = await this.requester.getAsync(TriggerbeeApi.Gateway, '/consent/widget/' + siteId);
    if (response.isNotFound()) {
      this.logger.info(`Widget does not exist with id ${siteId}`);
      return false;
    }
    if (!response.isPositive()) {
      this.logger.error(`Widget could not be get : ${response.toString()}`);
      return false;
    }
    return true;
  }
}
